import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import {
  applyUserPreferences,
  getUserPreferences,
  saveUserPreferences,
  subscribeUserPreferences
} from '../services/userPreferences';
import { useAuth } from '../services/AuthContext';
import { buildStyledTheme } from '../styles/themePalettes';
import GlobalThemeStyles from '../styles/GlobalThemeStyles';

const ThemePreferencesContext = createContext(null);

export function ThemePreferencesProvider({ children }) {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState(() => getUserPreferences());

  const styledTheme = useMemo(
    () => buildStyledTheme(preferences.theme),
    [preferences.theme]
  );

  const applyPreferences = useCallback(async (next) => {
    const merged = await saveUserPreferences(next, {
      user: user?.id ? user : null,
      syncRemote: Boolean(user?.id && user?.role)
    });
    setPreferences(merged);
    return merged;
  }, [user]);

  useEffect(() => {
    applyUserPreferences(preferences);
  }, [preferences]);

  useEffect(() => {
    return subscribeUserPreferences((next) => {
      setPreferences(next);
    });
  }, []);

  useEffect(() => {
    if (preferences.theme !== 'auto') return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyUserPreferences(getUserPreferences());
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [preferences.theme]);

  const value = useMemo(
    () => ({
      preferences,
      colors: styledTheme.colors,
      mode: styledTheme.mode,
      themePreference: preferences.theme,
      language: preferences.language,
      setTheme: (theme) => applyPreferences({ theme }),
      setLanguage: (language) => applyPreferences({ language }),
      updatePreferences: applyPreferences
    }),
    [preferences, styledTheme, applyPreferences]
  );

  return (
    <ThemePreferencesContext.Provider value={value}>
      <StyledThemeProvider theme={styledTheme}>
        <GlobalThemeStyles />
        {children}
      </StyledThemeProvider>
    </ThemePreferencesContext.Provider>
  );
}

export function useThemePreferences() {
  const ctx = useContext(ThemePreferencesContext);
  if (!ctx) {
    throw new Error('useThemePreferences doit être utilisé dans ThemePreferencesProvider');
  }
  return ctx;
}

/** Alias pour les composants qui n'ont besoin que de la palette active. */
export function useAppColors() {
  return useThemePreferences().colors;
}

export default ThemePreferencesContext;
